import { useEffect, useState } from 'react';
import { generateAlert } from '../utils/riskCalculator';

/**
 * AlertModal component for displaying threshold breach notifications
 */
export default function AlertModal({ alerts = [], onDismiss, onDismissAll }) {
  const [visible, setVisible] = useState(false);
  const [activeAlerts, setActiveAlerts] = useState([]);

  useEffect(() => {
    if (alerts.length > 0) {
      setActiveAlerts(alerts);
      setVisible(true);
    }
  }, [alerts]);

  const handleDismiss = (index) => {
    const newAlerts = activeAlerts.filter((_, i) => i !== index);
    setActiveAlerts(newAlerts);
    
    if (newAlerts.length === 0) {
      setVisible(false);
    }
    
    if (onDismiss) {
      onDismiss(index);
    }
  };

  const handleDismissAll = () => {
    setActiveAlerts([]);
    setVisible(false);
    if (onDismissAll) {
      onDismissAll();
    }
  };

  if (!visible || activeAlerts.length === 0) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-20 px-4">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleDismissAll}
      />

      {/* Modal */}
      <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden animate-slide-down">
        {/* Header */}
        <div className="bg-danger text-white px-6 py-4 flex items-center justify-between">
          <div className="flex items-center">
            <svg className="w-6 h-6 mr-3" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <h2 className="text-xl font-bold">
              Risk Alerts ({activeAlerts.length})
            </h2>
          </div>
          <button
            onClick={handleDismissAll}
            className="text-white hover:text-gray-200 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Alerts list */}
        <div className="overflow-y-auto max-h-[60vh] p-6 space-y-4">
          {activeAlerts.map((alert, index) => {
            const alertData = generateAlert(alert.metric, alert.value, alert.status);
            
            return (
              <div
                key={index}
                className={`p-4 rounded-lg border-l-4 ${
                  alert.status === 'red'
                    ? 'bg-danger/10 border-danger'
                    : 'bg-warning/10 border-warning'
                }`}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      <span className={`px-2 py-1 rounded text-xs font-semibold text-white ${
                        alert.status === 'red' ? 'bg-danger' : 'bg-warning'
                      }`}>
                        {alert.status.toUpperCase()}
                      </span>
                      <span className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300 capitalize">
                        {alert.metric.replace(/([A-Z])/g, ' $1').trim()}
                      </span>
                    </div>
                    <p className="text-sm text-gray-800 dark:text-gray-200">
                      {alertData?.message}
                    </p>
                    <p className="text-xs text-gray-500 mt-2">
                      {new Date(alert.timestamp).toLocaleString()}
                    </p>
                  </div>
                  <button
                    onClick={() => handleDismiss(index)}
                    className="ml-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer with actions */}
        <div className="bg-gray-50 dark:bg-gray-700 px-6 py-4 flex justify-between items-center">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Review your position and consider risk mitigation strategies
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleDismissAll}
              className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors text-sm font-medium"
            >
              Dismiss All
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Mini alert toast component for less intrusive notifications
 */
export function AlertToast({ alert, onClose }) {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(() => onClose && onClose(), 300);
    }, 5000);

    return () => clearTimeout(timer);
  }, [onClose]);

  if (!isVisible) return null;

  const alertData = generateAlert(alert.metric, alert.value, alert.status);

  return (
    <div className={`fixed bottom-4 right-4 max-w-md bg-white dark:bg-gray-800 rounded-lg shadow-xl border-l-4 ${
      alert.status === 'red' ? 'border-danger' : 'border-warning'
    } p-4 animate-slide-up z-50`}>
      <div className="flex items-start">
        <div className={`flex-shrink-0 ${alert.status === 'red' ? 'text-danger' : 'text-warning'}`}>
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        </div>
        <div className="ml-3 flex-1">
          <p className="text-sm font-medium text-gray-900 dark:text-white capitalize">
            {alert.metric.replace(/([A-Z])/g, ' $1').trim()}
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            {alertData?.message}
          </p>
        </div>
        <button
          onClick={() => {
            setIsVisible(false);
            setTimeout(() => onClose && onClose(), 300);
          }}
          className="ml-4 text-gray-400 hover:text-gray-600"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
}

